import { GlobalStyles as MuiGlobalStyles, type Theme } from "@schaeffler-commerce/design-system";

/**
 * Migrated from styles/react/externalContent/st4Content.less.
 *
 * Typography and table styles for injected external content under
 * #st4content (StaticContentPageTemplate) and .jsp__user-generated-content
 * (ContentPage1PageTemplate, CMSParagraph).
 *
 * The `@layer external-content` wrapper is LOAD-BEARING (SCHAEFF-31488): it
 * demotes these rules below all unlayered CSS — bootstrap/normalize via
 * style_vendor.less AND MUI runtime styles — so React-rendered components
 * inside the injected content keep their own typography. Emotion emits the
 * layer through the "@layer" at-rule object key; the layer order declaration
 * still lives in style.less (@layer resets, utilities, external-content).
 */
export const st4ContentStyles = (theme: Theme) => ({
  "@layer external-content": {
    "#st4content, .jsp__user-generated-content": {
      "h1, .h1, h2, .h2, h3, .h3": {
        marginTop: "16px",
        marginBottom: "8px",
      },
      "h4, .h4, h5, .h5, h6, .h6": {
        marginTop: "8px",
        marginBottom: "8px",
      },
      "& p": {
        margin: "0 0 8px",
        ...theme.typography.bodyLarge,
      },
      "& h1": {
        ...theme.typography.displaySmall,
        margin: "1em 0",
        "&:first-child": {
          marginTop: 0,
        },
      },
      "& > *:first-child": {
        marginTop: "0 !important",
        paddingTop: "0 !important",
      },
      // also used by .scha__lc__external-content
      "& h2": {
        ...theme.typography.headlineMedium,
        paddingTop: "25px",
      },
      "& h3": {
        ...theme.typography.titleLarge,
      },
      "& h4": {
        ...theme.typography.titleMedium,
        fontWeight: "bold",
      },
      "& h5": {
        ...theme.typography.titleMedium,
      },
      "& h6": {
        ...theme.typography.titleMedium,
        fontWeight: "bold",
      },
      "& img": {
        maxWidth: "750px",
      },
      "& h1, & h2, & h3, & p": {
        "& img": {
          width: "5%",
          marginTop: "15px",
        },
      },
      "& table": {
        border: `1px solid ${theme.palette.divider}`,
        "& > thead": {
          "& > tr": {
            "& > th": {
              padding: "0 20px",
              ...theme.typography.bodyMedium,
              marginBottom: 0,
              background: theme.palette.divider,
              color: "black",
              textTransform: "none",
              "&:last-child": {
                textAlign: "left",
              },
              "& > *:last-child": {
                marginBottom: 0,
              },
            },
          },
          "& > tr:first-child > th": {
            paddingTop: "8px",
          },
          "& > tr:last-child > th": {
            paddingBottom: "8px",
          },
        },
        "& > tbody": {
          "& > tr": {
            "& > td": {
              padding: "8px 20px",
              ...theme.typography.bodyMedium,
              borderTop: `1px solid ${theme.palette.divider}`,
              "& > *:last-child": {
                marginBottom: 0,
              },
              "&:not(:last-child)": {
                borderRight: `1px solid ${theme.palette.divider}`,
              },
            },
          },
          "& > tr:nth-child(odd)": {
            background: theme.palette.surface.main,
          },
          "& > tr:nth-child(even)": {
            background: "white",
          },
        },
      },
      "& .content": {
        "& > h1, & > h2, & > h3": {
          "&:first-child": {
            marginTop: 0,
          },
        },
      },
    },
  },
});

export const St4ContentGlobalStyles = () => <MuiGlobalStyles styles={st4ContentStyles} />;
